import { createBrowserRouter } from "react-router";
import { SignInPage } from "../pages/sign-in";
import { Shell } from "./shell";

/**
 * The route table. `apps/admin` is a Vite SPA on react-router (docs/24 §14) —
 * there are no route groups and no server components; the shell is a layout
 * route and every page below it can assume a signed-in, passkey-backed operator.
 *
 * `/sign-in` sits OUTSIDE the shell: a shell that redirected to a page inside
 * itself is a redirect loop the first time a session expires. It is also the one
 * page imported eagerly — **every other page is loaded on demand**, because AG
 * Grid is most of this bundle and there is no reason for somebody who is not
 * signed in yet to download a data grid before they can see a password field.
 */
export const router = createBrowserRouter([
  { path: "/sign-in", element: <SignInPage /> },
  {
    path: "/",
    element: <Shell />,
    children: [
      { index: true, lazy: async () => ({ Component: (await import("../pages/dashboard")).DashboardPage }) },
      // The enrolment screen is inside the shell so the operator can see who
      // they are signed in as while they register a passkey.
      { path: "enrol", lazy: async () => ({ Component: (await import("../pages/enrol")).EnrolPage }) },
      { path: "merchants", lazy: async () => ({ Component: (await import("../pages/merchants/register")).MerchantsPage }) },
      { path: "merchants/commission", lazy: async () => ({ Component: (await import("../pages/merchants/commission")).CommissionPage }) },
      { path: "catalog", lazy: async () => ({ Component: (await import("../pages/catalog/products")).CatalogProductsPage }) },
      { path: "catalog/taxonomy", lazy: async () => ({ Component: (await import("../pages/catalog/taxonomy")).TaxonomyPage }) },
      { path: "orders", lazy: async () => ({ Component: (await import("../pages/orders")).OrdersPage }) },
      { path: "dispatch", lazy: async () => ({ Component: (await import("../pages/dispatch")).DispatchPage }) },
      { path: "disputes", lazy: async () => ({ Component: (await import("../pages/disputes")).DisputesPage }) },
      { path: "finance/payables", lazy: async () => ({ Component: (await import("../pages/finance/payables")).PayablesPage }) },
      { path: "finance/payouts", lazy: async () => ({ Component: (await import("../pages/finance/payouts")).PayoutsPage }) },
      { path: "finance/trial-balance", lazy: async () => ({ Component: (await import("../pages/finance/trial-balance")).TrialBalancePage }) },
      { path: "finance/tie-outs", lazy: async () => ({ Component: (await import("../pages/finance/tie-outs")).TieOutsPage }) },
      { path: "tax/issuers", lazy: async () => ({ Component: (await import("../pages/tax/issuers")).IssuersPage }) },

      // ── Wave 5: the three desks where a person decides about a person ─────
      { path: "risk", lazy: async () => ({ Component: (await import("../pages/risk/cases")).RiskCasesPage }) },
      { path: "risk/rules", lazy: async () => ({ Component: (await import("../pages/risk/rules")).RiskRulesPage }) },
      { path: "risk/lists", lazy: async () => ({ Component: (await import("../pages/risk/lists")).RiskListsPage }) },
      { path: "support", lazy: async () => ({ Component: (await import("../pages/support/queue")).SupportQueuePage }) },
      { path: "support/nps", lazy: async () => ({ Component: (await import("../pages/support/satisfaction")).SatisfactionPage }) },
      { path: "support/articles", lazy: async () => ({ Component: (await import("../pages/support/articles")).HelpArticlesPage }) },
      { path: "moderation", lazy: async () => ({ Component: (await import("../pages/moderation")).ModerationPage }) },

      // ── Wave 5: the programmes that spend money to bring people back ──────
      { path: "loyalty", lazy: async () => ({ Component: (await import("../pages/loyalty")).LoyaltyPage }) },
      { path: "affiliate", lazy: async () => ({ Component: (await import("../pages/affiliate")).AffiliatePage }) },
      { path: "events", lazy: async () => ({ Component: (await import("../pages/events")).EventsPage }) },

      { path: "analytics", lazy: async () => ({ Component: (await import("../pages/analytics")).AnalyticsPage }) },
      { path: "settings", lazy: async () => ({ Component: (await import("../pages/settings")).SettingsPage }) },
      { path: "settings/flags", lazy: async () => ({ Component: (await import("../pages/settings/flags")).FlagsPage }) },
      { path: "settings/dated", lazy: async () => ({ Component: (await import("../pages/settings/dated")).DatedSettingsPage }) },
      { path: "settings/audit", lazy: async () => ({ Component: (await import("../pages/settings/audit")).AuditPage }) },
      { path: "settings/system", lazy: async () => ({ Component: (await import("../pages/settings/system")).SystemPage }) },
      // The manual glyph gate: Ө and Ү drawn large in the console's own face,
      // so decision 14b can be checked by eye on the machine that matters.
      { path: "font-check", lazy: async () => ({ Component: (await import("../pages/font-check")).FontCheckPage }) },
      { path: "*", lazy: async () => ({ Component: (await import("../pages/not-found")).NotFoundPage }) },
    ],
  },
]);
